<template>
  <CTable
    :align="align"
    responsive
    hover
    :class="tableClass"
    :style="tableStyle"
    bordered
  >
    <colgroup v-if="resizable">
      <col v-for="(_label, key) in columns" :key="key" :style="colStyle(key)" />
    </colgroup>
    <CTableCaption v-if="captions !== ''">{{ captions }}</CTableCaption>
    <HeaderRowWithProps
      :columns="columns"
      :extras="extras"
      :isEmpty="isEmpty"
      :with-sorter="withSorter"
      v-if="headerWithProps"
      @sort:column="(value) => $emit('sort:column', value)"
    />
    <HeaderRow
      :columns="columns"
      :with-sorter="withSorter"
      :resizable="resizable"
      :column-widths="columnWidths"
      @sort:column="(value) => $emit('sort:column', value)"
      @resize:column="(value) => $emit('resize:column', value)"
      @resize:end="$emit('resize:end')"
      v-else
    />
    <TableSkeleton
      :columns="headerWithProps ? spans || columns : columns"
      :rows="rows"
      v-if="isLoading"
    />
    <CTableBody v-else>
      <CTableRow v-if="isEmpty">
        <BodyRowEmpty :colspan="Object.keys(columns).length" />
      </CTableRow>
      <slot name="list" v-else></slot>
    </CTableBody>
    <CTableFoot>
      <slot name="footer"></slot>
    </CTableFoot>
  </CTable>
</template>

<script>
import HeaderRow from '@/components/tables/common/HeaderRow.vue'
import HeaderRowWithProps from '@/components/tables/common/HeaderRowWithProps.vue'
import BodyRowEmpty from '@/components/tables/common/BodyRowEmpty.vue'
import TableSkeleton from '@/components/tables/common/Skeleton.vue'

export default {
  name: 'TableLayout',

  components: {
    HeaderRow,
    HeaderRowWithProps,
    BodyRowEmpty,
    TableSkeleton,
  },

  props: {
    withSorter: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    columns: {
      required: true,
    },
    extras: {
      required: false,
    },
    spans: {
      type: Number,
      required: false,
    },
    isEmpty: {
      type: Boolean,
      required: true,
    },
    captions: {
      type: String,
      default: '',
    },
    className: {
      type: String,
      default: '',
    },
    resizable: {
      type: Boolean,
      default: false,
    },
    columnWidths: {
      type: Object,
      default: () => ({}),
    },
    tableWidth: {
      type: Number,
      default: 0,
    },
    headerWithProps: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 15,
    },
  },

  data() {
    return {
      align: 'middle',
      footer: ['Footer', 'Footer', 'Footer'],
    }
  },

  computed: {
    tableClass() {
      return [this.className, { 'table-resizable': this.resizable }]
    },
    tableStyle() {
      if (!this.resizable || !this.tableWidth) {
        return undefined
      }
      return { width: `${this.tableWidth}px` }
    },
  },

  methods: {
    colStyle(key) {
      const width = Number(this.columnWidths?.[key])
      if (!width) {
        return {}
      }
      return { width: `${width}px` }
    },
  },

  emits: ['sort:column', 'resize:column', 'resize:end'],
}
</script>

<style lang="scss">
.table-resizable,
table.table-resizable {
  table-layout: fixed;
}

.table-resizable > table {
  table-layout: fixed;
  width: 100%;
}

.table-resizable th,
.table-resizable td,
table.table-resizable th,
table.table-resizable td {
  width: var(--table-col-width) !important;
  min-width: var(--table-col-width) !important;
  max-width: var(--table-col-width) !important;
  overflow: hidden;
  vertical-align: middle;
}

.table-resizable td,
table.table-resizable td {
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
